import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  Explain,
  Loading,
  Modal,
  Mono,
  Screen,
  Stat,
} from '../../components/ui.js';
import { ManagerApproval, approvalRefusal, isOverridable } from '../auth/manager-approval.js';
import type { ManagerOverride } from '../auth/manager-approval.js';
import { useNotice } from '../../components/notice.js';
import { SearchSelect } from '../../components/search-select.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { useSearchTyping } from '../../lib/typing-focus.js';
import './receiving.css';

/**
 * Backorders (SCOPE §6.1).
 *
 * "A dedicated view of all items not yet received (filter by vendor/PO/age).
 * One-click cancel of some or all backordered lines (with reason), which closes
 * the PO cleanly."
 *
 * **A working screen, not a report.** The reports engine could group these
 * numbers, but nobody opens this to read a total — they open it to decide what
 * the vendor is never going to send, and to write it off. So it carries one
 * action per row and the totals sit above as context.
 *
 * The list asks the honest question: **outstanding quantity, not order status**.
 * This view returned nothing at all for weeks because it filtered on
 * `status IN ('submitted','partially_received')` while an imported order is
 * created `draft` and nothing ever moves it on — the submitting happened in the
 * vendor's own system before the file was downloaded (§5).
 *
 * Step budget: cancelling a backordered line is **≤3** — Cancel, pick a reason,
 * confirm. A manager PIN is one more, and only when an associate is doing it.
 */

const AGE_FILTERS: ReadonlyArray<{
  readonly key: string;
  readonly label: string;
  readonly days: number | null;
}> = [
  { key: 'any', label: 'Any age', days: null },
  { key: '14', label: '14 days +', days: 14 },
  { key: '30', label: '30 days +', days: 30 },
  { key: '60', label: '60 days +', days: 60 },
];

/** Why a buyer gives up on units. Four chips cover the real cases (decision 35). */
const REASONS: readonly string[] = [
  'Vendor canceled',
  'Out of stock at vendor',
  'Too late for the season',
  'No longer wanted',
];

interface CancelTarget {
  readonly lineId: string;
  readonly description: string;
  readonly number: string;
  readonly outstanding: number;
}

export function Backorders({ nav }: { nav?: ReactNode }) {
  const queryClient = useQueryClient();
  const [vendorId, setVendorId] = useState('');
  const [term, setTerm] = useState('');
  /** Typing anywhere here types into the search (client, 2026-10-15). */
  const searchRef = useSearchTyping(setTerm);
  const [age, setAge] = useState('any');
  const [target, setTarget] = useState<CancelTarget | null>(null);

  const minDaysOld = AGE_FILTERS.find((entry) => entry.key === age)?.days ?? null;

  const backorders = useQuery({
    queryKey: ['purchasing', 'backorders', vendorId, term, age],
    queryFn: () =>
      api.purchasing.backorders.query({
        vendorId: vendorId === '' ? null : vendorId,
        term: term === '' ? undefined : term,
        minDaysOld,
      }),
  });

  const taxonomy = useQuery({
    queryKey: ['products', 'taxonomy'],
    queryFn: () => api.products.taxonomy.query(),
    staleTime: 300_000,
  });

  const rows = backorders.data?.rows ?? [];
  const units = rows.reduce((total, row) => total + row.outstanding, 0);
  const costCents = rows.reduce((total, row) => total + row.outstanding * row.unitCostCents, 0);

  return (
    <Screen
      title="Backorders"
      subtitle="Everything ordered and not yet in the building."
      width="wide"
    >
      {nav}

      {backorders.isError ? (
        <Callout tone="danger">{errorMessage(backorders.error)}</Callout>
      ) : null}

      <div className="receiving__stats">
        <Stat
          label={
            <>
              Units outstanding{' '}
              <Explain label="units outstanding">
                Ordered minus received minus canceled, summed over every line shown. A purchase
                order that has fully arrived drops out on its own arithmetic rather than on its
                status.
              </Explain>
            </>
          }
          value={units}
        />
        <Stat
          label={
            <>
              At cost{' '}
              <Explain label="outstanding cost">
                Outstanding units × the unit cost negotiated on the purchase order line — not
                today&rsquo;s weighted average, because this is money not yet spent.
              </Explain>
            </>
          }
          value={money(costCents)}
        />
        <Stat label="Lines" value={rows.length} />
      </div>

      <div className="backorder__filters">
        <div className="field">
          <label className="field__label" htmlFor="backorder-vendor">
            Vendor
          </label>
          <SearchSelect
            id="backorder-vendor"
            noneLabel="All vendors"
            placeholder="All vendors"
            value={vendorId}
            options={(taxonomy.data?.vendors ?? []).map((vendor) => ({
              value: vendor.id,
              label: vendor.name,
            }))}
            onChange={setVendorId}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="backorder-term">
            PO, product or SKU
          </label>
          <input
            id="backorder-term"
            ref={searchRef}
            className="input search__input"
            value={term}
            placeholder="Search"
            onChange={(event) => setTerm(event.target.value)}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="backorder-age">
            Outstanding for
          </label>
          <select
            id="backorder-age"
            className="select"
            value={age}
            onChange={(event) => setAge(event.target.value)}
          >
            {AGE_FILTERS.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {backorders.data?.truncated === true ? (
        <Callout tone="warning" title="Showing the oldest 200">
          There are more outstanding lines than fit on one screen. Narrow by vendor or age to see
          the rest — the totals above cover what is shown, not the whole book.
        </Callout>
      ) : null}

      <Card padded={false}>
        {backorders.isPending ? (
          <Loading label="Loading backorders" />
        ) : rows.length === 0 ? (
          <p className="text-secondary receiving__empty">
            {vendorId === '' && term === '' && minDaysOld === null
              ? 'Nothing is outstanding. Every order placed has arrived or been closed.'
              : 'Nothing outstanding matches those filters.'}
          </p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>PO</th>
                  <th>Vendor</th>
                  <th className="num">Waiting</th>
                  <th className="num">Outstanding</th>
                  <th className="num">Unit cost</th>
                  <th className="num">Extension</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.lineId}>
                    <td>
                      {row.description}
                      <div className="text-secondary">
                        <Mono>{row.sku}</Mono>
                      </div>
                    </td>
                    <td>
                      <Mono>{row.number}</Mono>
                    </td>
                    <td>{row.vendorName}</td>
                    <td className="num">
                      {row.daysOld >= 30 ? (
                        <Badge tone="warning">{row.daysOld} days</Badge>
                      ) : (
                        <span className="text-secondary">{row.daysOld} days</span>
                      )}
                    </td>
                    <td className="num">{row.outstanding}</td>
                    <td className="num money">{money(row.unitCostCents)}</td>
                    <td className="num money">{money(row.outstanding * row.unitCostCents)}</td>
                    <td className="num">
                      <Button
                        variant="secondary"
                        onClick={() =>
                          setTarget({
                            lineId: row.lineId,
                            description: row.description,
                            number: row.number,
                            outstanding: row.outstanding,
                          })
                        }
                      >
                        Cancel
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {target !== null ? (
        <CancelBackorder
          target={target}
          onClose={() => setTarget(null)}
          onDone={() => {
            setTarget(null);
            void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
          }}
        />
      ) : null}
    </Screen>
  );
}

/**
 * Give up on units that are never coming.
 *
 * Quantity defaults to the whole outstanding amount, because "the vendor is not
 * sending these" is the normal case and a partial cancellation is the exception
 * (§2.2-5). The reason is a tap; none is pre-selected, because a wrong reason
 * recorded by a blind Enter is worse than making someone choose (decision 35).
 */
function CancelBackorder({
  target,
  onClose,
  onDone,
}: {
  target: CancelTarget;
  onClose: () => void;
  onDone: () => void;
}) {
  const notify = useNotice();
  const [quantity, setQuantity] = useState(String(target.outstanding));
  const [reason, setReason] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);

  const parsed = Number.parseInt(quantity, 10);
  const validQuantity =
    Number.isInteger(parsed) && parsed >= 1 && parsed <= target.outstanding ? parsed : null;

  const cancel = useMutation({
    mutationFn: (override?: ManagerOverride) =>
      api.purchasing.cancelBackorder.mutate({
        purchaseOrderLineId: target.lineId,
        quantity: validQuantity ?? 0,
        reason: reason ?? '',
        ...(override === undefined ? {} : { override }),
      }),
    onSuccess: () => {
      recordBreadcrumb('purchasing', `cancelled ${validQuantity} on ${target.number}`);
      notify({
        title: `Backorder canceled — ${validQuantity ?? 0} × ${target.description} on ${target.number}`,
      });
      onDone();
    },
    onError: (error) => {
      // Not a refusal to act on — it is the prompt (§2-6).
      if (isOverridable(error)) setApproving(true);
    },
  });

  if (approving) {
    return (
      <ManagerApproval
        title="A manager approves this"
        because={`Writing off ${validQuantity ?? 0} of ${target.description} on ${target.number}.`}
        busy={cancel.isPending}
        error={cancel.isError ? approvalRefusal(cancel.error) : null}
        onCancel={() => {
          setApproving(false);
          cancel.reset();
        }}
        onApprove={(override) => cancel.mutate(override)}
      />
    );
  }

  return (
    <Modal
      open
      tone="danger"
      title="Cancel backordered units"
      description={`${target.description} — ${target.number}`}
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Keep waiting
        </Button>
      }
      primaryAction={
        <Button
          variant="danger"
          busy={cancel.isPending}
          disabledReason={
            validQuantity === null
              ? `Enter between 1 and ${target.outstanding}`
              : reason === null
                ? 'Pick a reason'
                : undefined
          }
          onClick={() => cancel.mutate(undefined)}
        >
          Cancel {validQuantity ?? 0} units
        </Button>
      }
    >
      <div className="field">
        <label className="field__label" htmlFor="backorder-qty">
          How many are you writing off?
        </label>
        <input
          id="backorder-qty"
          className="input num"
          inputMode="numeric"
          value={quantity}
          autoFocus
          onChange={(event) => setQuantity(event.target.value.replace(/\D/g, '').slice(0, 5))}
        />
        <p className="field__hint">
          {target.outstanding} outstanding. Canceling all of them closes this line, and the order
          can be signed off once nothing is left.
        </p>
      </div>

      <div className="field">
        <span className="field__label">Why?</span>
        <div className="chips">
          {REASONS.map((entry) => (
            <button
              key={entry}
              type="button"
              className={`chip${reason === entry ? ' chip--on' : ''}`}
              aria-pressed={reason === entry}
              onClick={() => setReason(entry)}
            >
              {entry}
            </button>
          ))}
        </div>
      </div>

      {cancel.isError && !isOverridable(cancel.error) ? (
        <Callout tone="danger">{errorMessage(cancel.error)}</Callout>
      ) : null}
    </Modal>
  );
}
